const API_URL = "/api";

const PAYMENT_LABELS = {
  cash: "Efectivo",
  card: "Tarjeta",
  transfer: "Transferencia",
  credit: "Crédito",
  mixed: "Pago mixto",
};

const money = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString("es-CO", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const getPublicUrl = (path) => {
  if (!path || typeof path !== "string") {
    return "";
  }

  return path.startsWith("http")
    ? path
    : `${API_URL}${path}`;
};

const getPaymentLabel = (paymentMethod) =>
  PAYMENT_LABELS[paymentMethod] || paymentMethod;

export const openSaleReceiptPrintWindow = () => {
  const printWindow = window.open(
    "",
    "_blank",
    "popup=yes,width=460,height=760"
  );

  if (!printWindow) {
    return null;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <title>Generando recibo...</title>
      </head>
      <body>
        Generando recibo...
      </body>
    </html>
  `);

  printWindow.document.close();

  return printWindow;
};

export const printSaleReceipt = (
  receipt,
  printWindow = null
) => {
  const outputWindow =
    printWindow || openSaleReceiptPrintWindow();

  if (!outputWindow) {
    return false;
  }

  const company = receipt?.company || {};
  const electronicInvoice =
    receipt?.electronic_invoice || {};

  const companyName =
    company.commercial_name ||
    company.company_name ||
    "Empresa";

  const companyAddress = [
    company.address,
    company.city,
  ]
    .filter(Boolean)
    .join(" · ");

  const companyContact = [
    company.phone,
    company.mobile,
    company.email,
  ]
    .filter(Boolean)
    .join(" · ");

  const logoUrl = getPublicUrl(company.logo);

  const logoHtml = logoUrl
    ? `
      <img
        class="receipt-logo"
        src="${escapeHtml(logoUrl)}"
        alt="Logo de ${escapeHtml(companyName)}"
      />
    `
    : "";

  const itemsHtml = (receipt.items || [])
    .map((item) => {
      const itemTotal =
        item.total ?? item.subtotal ?? 0;

      const itemTax = Number(item.tax_amount || 0);

      const baseUnitPrice = Number(
        item.base_unit_price ?? item.unit_price ?? 0
      );

      const finalUnitPrice = Number(
        item.unit_price ?? 0
      );

      const discountTotal = Number(
        item.discount_total || 0
      );

      const hasPromotion =
        Boolean(item.promotion_name) ||
        discountTotal > 0 ||
        baseUnitPrice > finalUnitPrice;

      return `
      <tr>
        <td class="item-description">
          <strong>
            ${escapeHtml(item.product_name)}
          </strong>

          ${item.sku
          ? `
                <small>
                  Ref.: ${escapeHtml(item.sku)}
                </small>
              `
          : ""
        }

          ${hasPromotion
          ? `
                <small class="original-price">
                  Precio original:
                  <s>${money(baseUnitPrice)}</s>
                </small>

                ${item.promotion_name
            ? `
                      <small class="promotion-name">
                        Promoción:
                        ${escapeHtml(item.promotion_name)}
                      </small>
                    `
            : ""
          }

                ${discountTotal > 0
            ? `
                      <small class="promotion-discount">
                        Descuento:
                        -${money(discountTotal)}
                      </small>
                    `
            : ""
          }
              `
          : ""
        }

          <small>
            ${Number(item.quantity)} ×
            ${money(finalUnitPrice)}
          </small>

          ${itemTax > 0
          ? `
                <small>
                  ${escapeHtml(item.tax_name || "Impuesto")}:
                  ${money(itemTax)}
                </small>
              `
          : ""
        }
        </td>

        <td class="item-total">
          ${money(itemTotal)}
        </td>
      </tr>
    `;
    })
    .join("");
  const paymentsHtml = (receipt.payments || [])
    .map(
      (payment) => `
        <div class="receipt-row">
          <span>
            ${escapeHtml(
        getPaymentLabel(payment.payment_method)
      )}
          </span>

          <strong>${money(payment.amount)}</strong>
        </div>
      `
    )
    .join("");

  const isElectronic =
    electronicInvoice.is_electronic === true;

  const electronicHtml = isElectronic
    ? `
      <section class="electronic-invoice">
        <h3>Factura electrónica de venta</h3>

        ${electronicInvoice.prefix ||
      electronicInvoice.number
      ? `
              <p>
                <strong>Documento:</strong>
                ${escapeHtml(
        `${electronicInvoice.prefix || ""}${electronicInvoice.number || ""
        }`
      )}
              </p>
            `
      : ""
    }

        ${electronicInvoice.cufe
      ? `
              <p class="cufe">
                <strong>CUFE:</strong>
                ${escapeHtml(electronicInvoice.cufe)}
              </p>
            `
      : ""
    }

        ${electronicInvoice.qr_url
      ? `
              <img
                class="receipt-qr"
                src="${escapeHtml(
        electronicInvoice.qr_url
      )}"
                alt="Código QR de factura electrónica"
              />
            `
      : ""
    }

        <p>
          Estado:
          ${escapeHtml(
      electronicInvoice.status || "Generada"
    )}
        </p>
      </section>
    `
    : `
      <section class="receipt-document-note">
        <strong>Recibo de venta POS</strong>
        <p>
          ${electronicInvoice.message ||
    "Documento no válido como factura electrónica."
    }
        </p>
      </section>
    `;

  const discount = Number(receipt.discount || 0);
  const tax = Number(receipt.tax || 0);
  const saleNumber = receipt.document_number || `#${receipt.sale_id}`;

  outputWindow.document.open();

  outputWindow.document.write(`
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="UTF-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />

        <title>
          Recibo de venta ${saleNumber}
        </title>

        <style>
          @page {
            size: 80mm auto;
            margin: 0;
          }

          * {
            box-sizing: border-box;
          }

          html,
          body {
            width: 80mm;
            margin: 0;
            padding: 0;
            color: #172033;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 11px;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .receipt {
            width: 80mm;
            padding: 6mm 5mm 7mm;
          }

          .center {
            text-align: center;
          }

          .receipt-logo {
            display: block;
            max-width: 46mm;
            max-height: 28mm;
            margin: 0 auto 3mm;
            object-fit: contain;
          }

          h1 {
            margin: 0;
            font-size: 16px;
            line-height: 1.25;
          }

          h2 {
            margin: 2mm 0 0;
            font-size: 11px;
            font-weight: 800;
            letter-spacing: 0.5px;
          }

          h3 {
            margin: 0 0 2mm;
            font-size: 11px;
          }

          p {
            margin: 1.2mm 0;
            line-height: 1.4;
          }

          .company-detail,
          .receipt-meta,
          .cashier {
            color: #4b5563;
            font-size: 10px;
          }

          .receipt-meta {
            margin-top: 4mm;
          }

          hr {
            margin: 4mm 0;
            border: 0;
            border-top: 1px dashed #9ca3af;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          td {
            padding: 2.2mm 0;
            vertical-align: top;
          }

          .item-description {
            width: 72%;
            padding-right: 3mm;
          }

          .item-description strong,
          .item-description small {
            display: block;
          }
          .original-price {
  color: #6b7280;
}

.original-price s {
  color: #9ca3af;
}

.promotion-name {
  color: #b00083 !important;
  font-weight: 700;
}

.promotion-discount {
  color: #078b59 !important;
  font-weight: 700;
}

          .item-description small {
            margin-top: 0.7mm;
            color: #6b7280;
            font-size: 9px;
          }

          .item-total {
            width: 28%;
            text-align: right;
            font-weight: 700;
            white-space: nowrap;
          }

          .receipt-row {
            display: flex;
            justify-content: space-between;
            gap: 4mm;
            margin: 1.8mm 0;
          }

          .receipt-total {
            margin-top: 3mm;
            padding-top: 3mm;
            border-top: 2px solid #172033;
            font-size: 15px;
            font-weight: 800;
          }

          .receipt-document-note,
          .electronic-invoice {
            margin-top: 5mm;
            padding: 3mm;
            background: #f3f6fa;
            border-left: 3px solid var(--company-primary);
          }

          .receipt-document-note p {
            color: #4b5563;
            font-size: 9px;
          }

          .electronic-invoice {
            border-left-color: #0f766e;
          }

          .cufe {
            overflow-wrap: anywhere;
            font-size: 8px;
          }

          .receipt-qr {
            display: block;
            width: 28mm;
            height: 28mm;
            margin: 3mm auto;
            object-fit: contain;
          }

          .thanks {
            margin-top: 6mm;
            font-size: 11px;
            font-weight: 800;
          }

          .system-footer {
            margin-top: 3mm;
            color: #6b7280;
            font-size: 8px;
          }
        </style>
      </head>

      <body>
        <main class="receipt">
          <header class="center">
            ${logoHtml}

            <h1>${escapeHtml(companyName)}</h1>

            ${company.nit
      ? `
                  <p class="company-detail">
                    ${escapeHtml(
        company.document_type || "NIT"
      )}:
                    ${escapeHtml(company.nit)}
                  </p>
                `
      : ""
    }

            ${companyAddress
      ? `
                  <p class="company-detail">
                    ${escapeHtml(companyAddress)}
                  </p>
                `
      : ""
    }

            ${companyContact
      ? `
                  <p class="company-detail">
                    ${escapeHtml(companyContact)}
                  </p>
                `
      : ""
    }

            <h2>
              ${escapeHtml(
      receipt.document_type ||
      "RECIBO DE VENTA POS"
    )}
            </h2>

            <div class="receipt-meta">
              <p>Venta ${escapeHtml(saleNumber)}</p>
              <p>${escapeHtml(formatDate(receipt.sale_date))}</p>
            </div>
          </header>

          <hr />

          <section>
            <p>
              <strong>Cliente:</strong>
              ${escapeHtml(
      receipt.customer_name || "Cliente general"
    )}
            </p>

            ${receipt.cashier_name
      ? `
                  <p class="cashier">
                    Cajero:
                    ${escapeHtml(receipt.cashier_name)}
                  </p>
                `
      : ""
    }
          </section>

          <hr />

          <table>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>

          <hr />

          <section>
            ${paymentsHtml}

            <div class="receipt-row">
              <span>Subtotal</span>
              <strong>${money(receipt.subtotal)}</strong>
            </div>

            ${discount > 0
      ? `
                  <div class="receipt-row">
                    <span>Descuento</span>
                    <strong>-${money(discount)}</strong>
                  </div>
                `
      : ""
    }

            ${tax > 0
      ? `
                  <div class="receipt-row">
                    <span>Impuestos</span>
                    <strong>${money(tax)}</strong>
                  </div>
                `
      : ""
    }

            <div class="receipt-row receipt-total">
              <span>TOTAL</span>
              <span>${money(receipt.total)}</span>
            </div>
          </section>

          ${electronicHtml}

          <footer class="center">
            <p class="thanks">
              ¡Gracias por su compra!
            </p>

            <p class="system-footer">
              Generado por ${escapeHtml(
      receipt.generated_by || "NetLedger"
    )}
            </p>
          </footer>
        </main>
      </body>
    </html>
  `);

  outputWindow.document.close();

  window.setTimeout(() => {
    outputWindow.focus();
    outputWindow.print();
  }, 400);

  outputWindow.onafterprint = () => {
    outputWindow.close();
  };

  return true;
};